/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

;(function () {
  var HOTSPOT_CARD_OFFSET = 20
  var MOBILE_STL_MQ = '(max-width: 740px)'
  var STL_MOB_DESIGN_W = 375
  var STL_MOB_GAP = 8

  function getShopTheLookMobileViewportWidth() {
    return window.innerWidth || document.documentElement.clientWidth || STL_MOB_DESIGN_W
  }

  function getShopTheLookMobileSwiperSpaceBetween() {
    return (getShopTheLookMobileViewportWidth() * STL_MOB_GAP) / STL_MOB_DESIGN_W
  }

  function updateShopTheLookSwiperSpacing(section) {
    if (!section || !window.matchMedia(MOBILE_STL_MQ).matches) {
      return
    }
    var swiper = section.__shopTheLookNewSwiper
    if (!swiper || !swiper.params) {
      return
    }
    swiper.params.spaceBetween = getShopTheLookMobileSwiperSpaceBetween()
    if (swiper.update) {
      swiper.update()
    }
  }

  function scheduleFrame(section, callback) {
    if (section.__shopTheLookNewRaf) {
      cancelAnimationFrame(section.__shopTheLookNewRaf)
    }

    section.__shopTheLookNewRaf = requestAnimationFrame(function () {
      section.__shopTheLookNewRaf = null
      callback()
    })
  }

  function positionCard(card) {
    if (!card || !card.__shopTheLookNewPortal) {
      return
    }

    var portalData = card.__shopTheLookNewPortal
    var overlayLayer = portalData.overlayLayer
    var anchorWrapper = portalData.anchorWrapper

    if (!overlayLayer || !anchorWrapper || !overlayLayer.isConnected || !anchorWrapper.isConnected) {
      return
    }

    var overlayRect = overlayLayer.getBoundingClientRect()
    var wrapperRect = anchorWrapper.getBoundingClientRect()
    var cardWidth = card.offsetWidth
    var left = wrapperRect.left - overlayRect.left + HOTSPOT_CARD_OFFSET

    if (card.classList.contains('shop-the-look-new__anchor-card--reverse')) {
      left = wrapperRect.left - overlayRect.left + wrapperRect.width - HOTSPOT_CARD_OFFSET - cardWidth
    }

    card.style.left = left + 'px'
    card.style.top = wrapperRect.top - overlayRect.top + HOTSPOT_CARD_OFFSET + 'px'
  }

  function positionSectionCards(section) {
    var cards = section.querySelectorAll('.shop-the-look-new__overlay-layer .shop-the-look-new__anchor-card')

    cards.forEach(function (card) {
      positionCard(card)
    })
  }

  function teardownShopTheLookNew(section) {
    if (section.__shopTheLookNewCleanup) {
      section.__shopTheLookNewCleanup()
      section.__shopTheLookNewCleanup = null
    }

    var overlay = section.querySelector('.shop-the-look-new__overlay-layer')
    if (overlay) {
      overlay.querySelectorAll('.shop-the-look-new__anchor-card').forEach(function (card) {
        var pd = card.__shopTheLookNewPortal
        if (pd && pd.anchorWrapper) {
          pd.anchorWrapper.appendChild(card)
        }
        card.__shopTheLookNewPortal = null
      })
    }

    section.__shopTheLookNewPortalReady = false
    section.__shopTheLookNewSchedulePosition = null

    if (section.__shopTheLookNewSwiper && section.__shopTheLookNewSwiper.destroy) {
      section.__shopTheLookNewSwiper.destroy(true, true)
      section.__shopTheLookNewSwiper = null
    }
  }

  function getShopTheLookAnchorScope(section) {
    if (window.matchMedia(MOBILE_STL_MQ).matches) {
      return section.querySelector('.shop-the-look-new__mobile-swipe')
    }
    return section.querySelector('.shop-the-look-new__desktop-scroll')
  }

  function updateShopTheLookTabNavLayout(nav) {
    if (!nav || !window.matchMedia(MOBILE_STL_MQ).matches) {
      return
    }
    nav.classList.remove('shop-the-look-new__tabs--scroll')
    if (nav.scrollWidth > nav.clientWidth) {
      nav.classList.add('shop-the-look-new__tabs--scroll')
    }
  }

  function scheduleShopTheLookTabNavLayout(section) {
    if (!section) {
      return
    }
    var nav = section.querySelector('.shop-the-look-new__tabs')
    if (!nav) {
      return
    }
    scheduleFrame(section, function () {
      updateShopTheLookTabNavLayout(nav)
    })
  }

  function syncShopTheLookTabsFromSwiper(section, swiper) {
    var n = parseInt(section.getAttribute('data-stl-look-count'), 10) || 0
    if (n < 1 || !swiper) {
      return
    }

    var logicalTab = swiper.realIndex % n
    var tabs = section.querySelectorAll('.shop-the-look-new__tab')
    for (var t = 0; t < tabs.length; t++) {
      if (t === logicalTab) {
        tabs[t].classList.add('is-active')
        tabs[t].setAttribute('aria-selected', 'true')
        tabs[t].setAttribute('tabindex', '0')
      } else {
        tabs[t].classList.remove('is-active')
        tabs[t].setAttribute('aria-selected', 'false')
        tabs[t].setAttribute('tabindex', '-1')
      }
    }
  }

  function initShopTheLookNewSwiper(section) {
    if (!window.matchMedia(MOBILE_STL_MQ).matches) {
      return
    }

    var container = section.querySelector('.shop-the-look-new__mobile-swipe .shop-the-look-new__swiper')
    if (!container || typeof window.Swiper === 'undefined') {
      return
    }

    var slides = container.querySelectorAll('.swiper-slide')
    if (slides.length === 0) {
      return
    }

    var lookCount = parseInt(section.getAttribute('data-stl-look-count'), 10) || 0
    if (slides.length < 2) {
      return
    }

    var nextEl = section.querySelector('.shop-the-look-new__swiper-button-next')
    var prevEl = section.querySelector('.shop-the-look-new__swiper-button-prev')

    var opts = {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: getShopTheLookMobileSwiperSpaceBetween(),
      watchSlidesProgress: true,
      on: {
        slideChange: function () {
          syncShopTheLookTabsFromSwiper(section, this)
          if (section.__shopTheLookNewSchedulePosition) {
            section.__shopTheLookNewSchedulePosition()
          }
        },
      },
    }

    if (lookCount > 2) {
      opts.loop = true
    }

    if (nextEl && prevEl) {
      opts.navigation = {
        nextEl: nextEl,
        prevEl: prevEl,
      }
    }

    section.__shopTheLookNewSwiper = new window.Swiper(container, opts)
    syncShopTheLookTabsFromSwiper(section, section.__shopTheLookNewSwiper)
  }

  function bindShopTheLookTabs(section) {
    var tabNav = section.querySelector('.shop-the-look-new__tabs')
    if (!tabNav || tabNav.__stlTabBound) {
      return
    }
    tabNav.__stlTabBound = true
    tabNav.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('.shop-the-look-new__tab')
      if (!btn || !section.contains(btn)) {
        return
      }
      var ix = parseInt(btn.getAttribute('data-stl-tab-index'), 10)
      if (isNaN(ix)) {
        return
      }
      var swiper = section.__shopTheLookNewSwiper
      if (!swiper) {
        return
      }
      e.preventDefault()
      if (swiper.params.loop) {
        swiper.slideToLoop(ix)
      } else {
        swiper.slideTo(ix)
      }
    })
  }

  function scrollShopTheLookDesktopToIndex(section, slideIndex) {
    if (window.matchMedia(MOBILE_STL_MQ).matches) {
      return
    }

    var scrollTrack = section.querySelector('.shop-the-look-new__desktop-scroll .product-list__inner.shop-the-look-new__grid')
    if (!scrollTrack) {
      return
    }

    var targetSlide = scrollTrack.querySelector(
      '.shop-the-look-new-block-wrap[data-stl-slide-index="' + slideIndex + '"]'
    )
    if (!targetSlide) {
      return
    }

    scrollTrack.scrollTo({
      left: targetSlide.offsetLeft,
      behavior: 'smooth',
    })
  }

  function selectShopTheLookEditorBlock(section, slideIndex) {
    if (isNaN(slideIndex) || slideIndex < 0) {
      return
    }

    if (section.scrollIntoView) {
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }

    var swiper = section.__shopTheLookNewSwiper
    if (swiper && window.matchMedia(MOBILE_STL_MQ).matches) {
      if (swiper.params.loop) {
        swiper.slideToLoop(slideIndex)
      } else {
        swiper.slideTo(slideIndex)
      }
    } else {
      scrollShopTheLookDesktopToIndex(section, slideIndex)
    }

    if (section.__shopTheLookNewSchedulePosition) {
      section.__shopTheLookNewSchedulePosition()
    }
  }

  function bindShopTheLookEditorSelection(section) {
    if (!window.Shopify || !window.Shopify.designMode || section.__stlEditorBound) {
      return
    }

    section.__stlEditorBound = true

    var onBlockSelect = function (event) {
      var selectedBlock =
        event.target &&
        event.target.closest &&
        (event.target.closest('[data-stl-editor-block][data-stl-slide-index]') ||
          event.target.closest('.shop-the-look-new-block-wrap[data-stl-slide-index]'))

      if (!selectedBlock || !section.contains(selectedBlock)) {
        return
      }

      var slideIndex = parseInt(selectedBlock.getAttribute('data-stl-slide-index'), 10)
      selectShopTheLookEditorBlock(section, slideIndex)
    }

    section.addEventListener('shopify:block:select', onBlockSelect)
  }

  function initShopTheLookNewSection(section) {
    if (!section) {
      return
    }

    var scope = getShopTheLookAnchorScope(section)
    var overlayLayer = section.querySelector('.shop-the-look-new__overlay-layer')

    if (!scope || !overlayLayer) {
      return
    }

    var scrollViewport = scope.querySelector('.scroller')
    var scrollTrack = scope.querySelector('.product-list__inner.shop-the-look-new__grid')
    var productList = scope.querySelector('.product-list')

    var anchorWrappers = scope.querySelectorAll('.shop-the-look-new__anchor-wrapper')

    anchorWrappers.forEach(function (anchorWrapper) {
      var anchorCard = anchorWrapper.querySelector('.shop-the-look-new__anchor-card')

      if (!anchorCard) {
        return
      }

      anchorCard.__shopTheLookNewPortal = {
        anchorWrapper: anchorWrapper,
        overlayLayer: overlayLayer,
      }

      if (anchorCard.parentElement !== overlayLayer) {
        overlayLayer.appendChild(anchorCard)
      }

      positionCard(anchorCard)
    })

    var schedulePosition = function () {
      scheduleFrame(section, function () {
        positionSectionCards(section)
      })
    }

    var closeSectionCards = function () {
      section
        .querySelectorAll('.shop-the-look-new__anchor-card[open]')
        .forEach(function (card) {
          card.style.pointerEvents = 'none'
          card.open = false
        })
    }

    section.__shopTheLookNewPortalReady = true
    section.__shopTheLookNewSchedulePosition = schedulePosition

    var cleanups = []

    if (scrollTrack) {
      scrollTrack.addEventListener('scroll', schedulePosition, { passive: true })
      cleanups.push(function () {
        scrollTrack.removeEventListener('scroll', schedulePosition)
      })
    }

    if (scrollViewport) {
      scrollViewport.addEventListener('scroll', schedulePosition, { passive: true })
      cleanups.push(function () {
        scrollViewport.removeEventListener('scroll', schedulePosition)
      })
    }

    if (productList) {
      productList.addEventListener('prev-next:prev', schedulePosition)
      productList.addEventListener('prev-next:next', schedulePosition)
      cleanups.push(function () {
        productList.removeEventListener('prev-next:prev', schedulePosition)
        productList.removeEventListener('prev-next:next', schedulePosition)
      })
    }

    var onWinResize = function () {
      schedulePosition()
    }
    window.addEventListener('resize', onWinResize)
    cleanups.push(function () {
      window.removeEventListener('resize', onWinResize)
    })

    var onOpenableOpen = function (event) {
      if (event.target.classList.contains('shop-the-look-new__anchor-card')) {
        event.target.style.pointerEvents = 'auto'
        schedulePosition()
      }
    }
    section.addEventListener('openable-element:open', onOpenableOpen)
    cleanups.push(function () {
      section.removeEventListener('openable-element:open', onOpenableOpen)
    })

    var onOpenableClose = function (event) {
      if (event.target.classList.contains('shop-the-look-new__anchor-card')) {
        event.target.style.pointerEvents = 'none'
      }
    }
    section.addEventListener('openable-element:close', onOpenableClose)
    cleanups.push(function () {
      section.removeEventListener('openable-element:close', onOpenableClose)
    })

    var onSectionPointerDown = function (event) {
      if (!section.querySelector('.shop-the-look-new__anchor-card[open]')) {
        return
      }

      var target = event.target
      if (
        target.closest('.shop-the-look-new__anchor-card') ||
        target.closest('.shop-the-look-new__anchor-btn')
      ) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      closeSectionCards()
    }
    section.addEventListener('pointerdown', onSectionPointerDown)
    cleanups.push(function () {
      section.removeEventListener('pointerdown', onSectionPointerDown)
    })

    section.__shopTheLookNewCleanup = function () {
      cleanups.forEach(function (fn) {
        fn()
      })
    }

    schedulePosition()
  }

  function initShopTheLookNew(root) {
    var scope = root || document
    var sections = scope.querySelectorAll('.shop-the-look-new-section')

    sections.forEach(function (section) {
      teardownShopTheLookNew(section)
      initShopTheLookNewSection(section)
      initShopTheLookNewSwiper(section)
      if (section.__shopTheLookNewSwiper && section.__shopTheLookNewSwiper.update) {
        section.__shopTheLookNewSwiper.update()
      }
      if (section.__shopTheLookNewSchedulePosition) {
        section.__shopTheLookNewSchedulePosition()
      }
      bindShopTheLookTabs(section)
      bindShopTheLookEditorSelection(section)
      scheduleShopTheLookTabNavLayout(section)
    })
  }

  var resizeTimer = null
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('.shop-the-look-new-section').forEach(function (section) {
        var next = window.matchMedia(MOBILE_STL_MQ).matches ? 'm' : 'd'
        if (section.__shopTheLookNewBreakpoint === next) {
          scheduleShopTheLookTabNavLayout(section)
          updateShopTheLookSwiperSpacing(section)
          if (section.__shopTheLookNewSchedulePosition) {
            section.__shopTheLookNewSchedulePosition()
          }
          return
        }
        section.__shopTheLookNewBreakpoint = next
        initShopTheLookNew(section)
      })
    }, 160)
  })

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('.shop-the-look-new-section').forEach(function (section) {
        section.__shopTheLookNewBreakpoint = window.matchMedia(MOBILE_STL_MQ).matches ? 'm' : 'd'
      })
      initShopTheLookNew(document)
    })
  } else {
    document.querySelectorAll('.shop-the-look-new-section').forEach(function (section) {
      section.__shopTheLookNewBreakpoint = window.matchMedia(MOBILE_STL_MQ).matches ? 'm' : 'd'
    })
    initShopTheLookNew(document)
  }

document.addEventListener('shopify:section:load', function (event) {
  initShopTheLookNew(event.target)
})
})()

;(function () {
  function syncFeaturedCollectionsPanels(root) {
    var scope = root || document

    scope.querySelectorAll('.shopify-section--featured-collections').forEach(function (section) {
      var tabsNav = section.querySelector('tabs-nav')
      var buttons = Array.from(section.querySelectorAll('tabs-nav button[aria-controls]'))

      if (!tabsNav || buttons.length === 0) {
        return
      }

      var activeButton =
        buttons.find(function (button) {
          return button.getAttribute('aria-expanded') === 'true'
        }) || buttons[0]
      var activePanelId = activeButton && activeButton.getAttribute('aria-controls')

      section.querySelectorAll('.featured-collections > product-list[id]').forEach(function (panel) {
        panel.hidden = panel.id !== activePanelId
      })

      if (tabsNav.__featuredCollectionsSynced) {
        return
      }

      tabsNav.__featuredCollectionsSynced = true
      tabsNav.addEventListener('tabs-nav:changed', function (event) {
        var currentButton = event.detail && event.detail.button
        var currentPanelId = currentButton && currentButton.getAttribute('aria-controls')

        section.querySelectorAll('.featured-collections > product-list[id]').forEach(function (panel) {
          panel.hidden = panel.id !== currentPanelId
        })
      })
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      syncFeaturedCollectionsPanels(document)
      requestAnimationFrame(function () {
        syncFeaturedCollectionsPanels(document)
      })
    })
  } else {
    syncFeaturedCollectionsPanels(document)
    requestAnimationFrame(function () {
      syncFeaturedCollectionsPanels(document)
    })
  }

  document.addEventListener('shopify:section:load', function (event) {
    syncFeaturedCollectionsPanels(event.target)
    requestAnimationFrame(function () {
      syncFeaturedCollectionsPanels(event.target)
    })
  })
})()
